/**
 * Файлы комментария. Картинки — превью с просмотром в полный размер,
 * как в файлах задачи; остальное — подпись, по нажатию скачивается.
 *
 * Пока автор правит комментарий, у каждого файла, который ему можно снять,
 * есть крестик: так из нескольких картинок убирают лишние, не удаляя
 * комментарий целиком. Убранный файл бледнеет и возвращается повторным
 * нажатием — снимается он только по «Сохранить».
 */

import { Paperclip, Undo2, X } from "lucide-react";

import { cn, type CommentFilesEdit, IconButton } from "@/shared/ui";

import type { Attachment } from "@taskflow/client-core/tasks";
import { isPreviewableImage } from "../attachmentRules";
import { AttachmentImage } from "./AttachmentImage";

export function CommentAttachments({
  files,
  onDownload,
  edit = null,
}: {
  files: Attachment[];
  onDownload: (id: string) => void;
  /** null — комментарий не редактируется, файлы только показываются. */
  edit?: CommentFilesEdit | null;
}) {
  if (files.length === 0) return null;
  return (
    <ul className="mt-1.5 flex flex-wrap items-center gap-1.5" aria-label="Файлы комментария">
      {files.map((file) => {
        const removed = edit?.removed.has(file.id) === true;
        return (
          <li key={file.id} className="flex items-center gap-0.5">
            <span className={cn(removed && "opacity-40")}>
              {isPreviewableImage(file) ? (
                <AttachmentImage file={file} onDownload={() => onDownload(file.id)} />
              ) : (
                <button
                  type="button"
                  className={cn(
                    "flex max-w-60 items-center gap-1 rounded-sm bg-sunken px-2 py-0.5 text-[11px] text-muted hover:text-fg",
                    removed && "line-through",
                  )}
                  title={`Скачать · ${file.sizeLabel}`}
                  onClick={() => onDownload(file.id)}
                >
                  <Paperclip className="size-3 shrink-0" aria-hidden />
                  <span className="truncate">{file.filename}</span>
                </button>
              )}
            </span>
            {edit !== null && file.canDelete && (
              <IconButton
                icon={removed ? Undo2 : X}
                label={removed ? `Вернуть ${file.filename}` : `Убрать ${file.filename}`}
                size="sm"
                disabled={edit.disabled}
                onClick={() => edit.toggle(file.id)}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
